import React, { memo, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { environment } from '../../../environments/environment';
import noItems from '../../../assets/Hamburger-pana 1.png';
import OrderItem from './OrderItem';
import { useTranslation } from 'react-i18next';
import FattyBattyCard from './FattyBattyCard';

const OrderList = memo(
  ({
    orders = [],
    isLoading = false,
    hasMore = false,
    onLoadMore,
    onViewDetails,
    onReorder,
    onCancelOrder,
    onRateOrder,
    checkLicenses,
  }) => {
    const [t] = useTranslation();

    // Memoized load more handler
    const handleLoadMore = useCallback(() => {
      if (onLoadMore && !isLoading) {
        onLoadMore();
      }
    }, [onLoadMore, isLoading]);

    // Memoized orders rendering
    const renderedOrders = useMemo(() => {
      return orders.map((order, index) => (
        <Box
          key={`${order.order_id}-${index}`}
          mb={index !== orders.length - 1 ? 2 : 0}
        >
          <FattyBattyCard
            order={order}
            onViewDetails={onViewDetails}
            order_id={ order.order_id}
            order_status={order.order_status}
          />
        </Box>
      ));
    }, [orders, onViewDetails]);

    if (isLoading && orders.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
          }}
        >
          <CircularProgress size={50} sx={{ color: environment.mainColor }} />
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              color: '#666',
              fontSize: '16px',
            }}
          >
            Loading your orders...
          </Typography>
        </Box>
      );
    }

    if (orders.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: '330px',
          }}
        >
          <img
            src={noItems}
            alt={`No orders`}
            style={{
              maxWidth: '363px',
              width: '100%',
              maxHeight: '330px',
              marginBottom: '20px',
              objectFit: 'cover',
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              marginBottom: '8px',
              color: '#172B4D',
              fontSize: {
                xs: '14px',
                md: '16px',
                lg: '20px',
              },
              fontFamily: 'Jost, sans-serif',
              textAlign: 'center',
            }}
          >
            {t('noOrder') || 'No orders found'}
          </Typography>
        </div>
      );
    }

    return (
      <Box sx={{}}>
        {/* Render all orders efficiently */}
        {renderedOrders}

        {/* Load More Button */}
        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={isLoading}
              sx={{
                fontFamily: 'Jost, sans-serif',
                borderColor: environment.mainColor,
                color: environment.mainColor,
                '&:hover': {
                  borderColor: environment.mainColor,
                  backgroundColor: 'rgba(255, 107, 53, 0.04)',
                },
                '&:disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading...
                </>
              ) : (
                t('loadMore') || 'Load More'
              )}
            </Button>
          </Box>
        )}
      </Box>
    );
  }
);

OrderList.displayName = 'OrderList';

export default OrderList;
